import { useDispatch } from 'react-redux';
import { Notifications } from '@mui/icons-material';
import React, { memo, useMemo, useState } from 'react';
import {
  Grid,
  MenuItem,
  Skeleton,
  Typography,
  Select,
  FormControl,
  Box,
} from '@mui/material';
import { buildTeamInfo } from 'components/UserProfile/utilities';
import { setForceOpenTeamOverviewModal } from 'store/slices/userDetailsSlice';
import { TeamMembersButton } from 'components/TeamOverview/components/TeamSelection/TeamMembersButton';
import { useTeamMembersButtonStyles } from 'components/TeamOverview/components/TeamSelection/styles';
import { getFromLS, saveToLS } from 'components/OrderSlidesList/utilities';
import { NOTIFICATIONS_COUNT_KEY } from 'components/TeamOverview/constants';

export const TeamSelectionCard = memo(
  ({ selectedTeam, setSelectedTeamUUID, teams, isTeamMembersActive }) => {
    const notificationsCountFromLS = parseInt(
      getFromLS(NOTIFICATIONS_COUNT_KEY) || 0,
      10,
    );
    const [notificationsCount, setNotificationsCount] = useState(
      notificationsCountFromLS,
    );

    const clearNotificationsCount = () => {
      saveToLS(NOTIFICATIONS_COUNT_KEY, 0);
      setNotificationsCount(0);
    };

    const teamInfo = useMemo(() => buildTeamInfo(selectedTeam), [selectedTeam]);
    const dispatch = useDispatch();
    const { classes } = useTeamMembersButtonStyles({
      isActive: isTeamMembersActive,
    });

    const handleOpenNotifications = () => {
      dispatch(setForceOpenTeamOverviewModal(true));
      clearNotificationsCount();
    };

    const teamSelector = selectedTeam ? (
      <FormControl>
        <Select
          labelId="team-label"
          id="team-select"
          disableUnderline
          sx={{
            '& .MuiSelect-select': {
              padding: 0,
              paddingLeft: '0.5rem',
              fontSize: '1.25rem',
              fontWeight: 500,
              lineHeight: '1.5rem',
            },
          }}
          value={selectedTeam?.uuid}
          label="Team"
          onChange={(e) =>
            setSelectedTeamUUID(
              teams.find((team) => team.uuid === e.target.value).uuid,
            )
          }
        >
          {teams?.map((team) => (
            <MenuItem key={team.uuid} value={team.uuid}>
              {team.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ) : (
      <Skeleton variant="rectangular" width={150} height="2rem" />
    );

    const subtitle = selectedTeam ? (
      teamInfo
    ) : (
      <Skeleton variant="text" width={150} height="0.9375rem" />
    );

    return (
      <Grid item xs={12}>
        <Box paddingY="0.75rem">
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            width="100%"
            alignItems="center"
          >
            <Box display="flex" flexDirection="column" alignItems="center">
              <Grid item>
                <Typography
                  display="flex"
                  fontSize="1.25rem"
                  fontWeight={500}
                  lineHeight="1.5rem"
                >
                  Welcome, {teamSelector}
                </Typography>
                <Typography color="textSecondary">{subtitle}</Typography>
              </Grid>
            </Box>
            <Box display="flex" justifyContent="right" width="100%">
              <TeamMembersButton
                team={selectedTeam}
                classes={classes}
                isActive={isTeamMembersActive}
              />
              <Box
                className={classes.notificationButton}
                onClick={() => handleOpenNotifications()}
              >
                <Notifications color="primary" />
                {!!notificationsCount && (
                  <Box className={classes.notificationBubble}>
                    {notificationsCount}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Grid>
    );
  },
);

TeamSelectionCard.displayName = 'TeamSelectionCard';
